import { FC, useEffect, useState } from "react";
import { Box, styled } from "@mui/material";
import { H4, H5 } from "../../../../components/Typography";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { ukoTheme } from "../../../../theme";
import {
  chartColors,
  covertFigure,
} from "../../../../pages/Analytics/models/config";

const theme = ukoTheme();

interface IHistoryMax12Months {
  data?: any;
}

const HistoryMax12Months: FC<IHistoryMax12Months> = (props) => {
  const [graphData, setGraphData] = useState<any[]>([]);

  useEffect(() => {
    const periods = Object.keys(props?.data).map((item: any) => {
      return {
        period: item,
        score: props?.data[item],
      };
    });
    setGraphData(periods);
  }, [props?.data]);

  const getXAxis = (data: Array<{ period: string; score: number }>) => {
    return data.map((t) => t.period);
  };

  const getChartSeries = (data: Array<{ period: string; score: number }>) => {
    let chartSeries: any = [];

    data.forEach((item) => {
      chartSeries.push(item.score);
    });

    return [
      {
        name: "Score",
        data: chartSeries,
      },
    ];
  };

  const chartOptions = getChartOptions(getXAxis(graphData), "Score", null, 2);

  return (
    <Box sx={{ height: "100%", marginTop: 2 }}>
      <H5>History Max 12 Months</H5>
      <ApexChartBox>
        <ApexChartWrapper>
          <Chart
            height={245}
            options={chartOptions}
            series={getChartSeries(graphData)}
            type="line"
          />
        </ApexChartWrapper>
      </ApexChartBox>
    </Box>
  );
};

export const getChartOptions = (
  getXAxis: any,
  yAxixText?: any,
  columnWidth?: number | null,
  stroke?: number | null,
  colors?: any[],
) => {
  const chartOptions: ApexOptions = {
    chart: {
      background: "transparent",
      toolbar: { show: false },
      id: "",
    },
    colors: colors || chartColors,
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: stroke || 0,
    },
    xaxis: {
      categories: getXAxis,
    },
    yaxis: {
      title: {
        text: yAxixText || "",
      },
      show: true,
      labels: {
        show: true,
        formatter: function (val: any) {
          return covertFigure(val);
        },
      },
    },
    responsive: [
      {
        breakpoint: 550,
        options: {
          chart: {
            height: 350,
          },
          plotOptions: {
            bar: {
              horizontal: true,
            },
          },
          xaxis: {
            labels: { show: false },
          },
          yaxis: {
            show: true,
            labels: {
              style: {
                colors: theme.palette.text.disabled,
                fontFamily: theme.typography.fontFamily,
                fontWeight: 500,
              },
              formatter: function (val: any) {
                return covertFigure(val);
              },
            },
          },
        },
      },
    ],
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 5,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
        columnWidth: columnWidth || 20,
      },
    },
  };
  return chartOptions;
};

export const ApexChartWrapper = styled(Box)(({ theme }) => ({
  "& .apexcharts-tooltip *": {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
  },
  "& .apexcharts-tooltip": {
    boxShadow: 0,
    borderRadius: 4,
    alignItems: "center",
    "& .apexcharts-tooltip-text-y-value": { color: "primary.main" },
    "& .apexcharts-tooltip.apexcharts-theme-light": {
      border: `1px solid ${theme.palette.divider}`,
    },
    "& .apexcharts-tooltip-series-group:last-child": {
      paddingBottom: 0,
    },
  },
}));

export const ApexChartBox = styled(Box)(({ theme }) => ({
  height: "100%",
  [theme.breakpoints.down(425)]: { padding: "1.5rem" },
}));

export default HistoryMax12Months;
